import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { formatEther, isAddress, type Address } from 'viem';
import { api } from './api';
import { ChainFarm } from './ChainFarm';
import { ranchNftAbi } from './contract';
import { GameAudio, playErrorSound, playSuccessSound, TutorialOverlay } from './GameUX';
import { ProjectTokenPanel } from './ProjectTokenPanel';
import type { GameState, LeaderboardEntry } from './types';
import { connectWallet, nstChain, publicClient, signLogin, walletClient } from './wallet';

type Tab = 'farm' | 'nft' | 'social' | 'market' | 'world' | 'profile';
const MarketHub = lazy(() => import('./MarketHub').then((module) => ({ default: module.MarketHub })));
const WorldHub = lazy(() => import('./WorldHub').then((module) => ({ default: module.WorldHub })));

function shortAddress(address: string) {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

async function loadSale(contractAddress: Address) {
  const [price, minted, supply, active] = await Promise.all([
    publicClient.readContract({ address: contractAddress, abi: ranchNftAbi, functionName: 'mintPrice' }),
    publicClient.readContract({ address: contractAddress, abi: ranchNftAbi, functionName: 'totalMinted' }),
    publicClient.readContract({ address: contractAddress, abi: ranchNftAbi, functionName: 'maxSupply' }),
    publicClient.readContract({ address: contractAddress, abi: ranchNftAbi, functionName: 'saleActive' })
  ]);
  return { price, minted, supply, active };
}

export function App() {
  const [tab, setTab] = useState<Tab>('farm');
  const [address, setAddress] = useState<Address | null>(null);
  const [state, setState] = useState<GameState | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [neighbor, setNeighbor] = useState('');
  const [name, setName] = useState('');
  const [sale, setSale] = useState<{ price: bigint; minted: bigint; supply: bigint; active: boolean } | null>(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const notify = (text: string) => {
    setMessage(text);
    setError('');
    playSuccessSound();
    window.setTimeout(() => setMessage(''), 3000);
  };
  const fail = (value: unknown) => {
    setError(value instanceof Error ? value.message : '操作失败，请稍后重试');
    setMessage('');
    playErrorSound();
  };

  const refresh = useCallback(async () => {
    const next = await api.getState();
    setState(next);
    setAddress(next.player.address as Address);
    setName(next.player.displayName);
  }, []);

  useEffect(() => {
    api.getLeaderboard().then(setLeaderboard).catch(() => undefined);
    if (api.hasToken()) refresh().catch(() => api.clearToken());
  }, [refresh]);

  useEffect(() => {
    if (tab !== 'nft' || !address) return;
    const contractAddress = import.meta.env.VITE_NFT_CONTRACT_ADDRESS;
    if (!contractAddress || !isAddress(contractAddress)) {
      setSale(null);
      return;
    }
    loadSale(contractAddress).then(setSale).catch(fail);
  }, [tab, address]);

  useEffect(() => {
    if (state && localStorage.getItem('nst-ranch-tutorial-complete') !== 'true') setTutorialOpen(true);
  }, [state]);

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    try {
      await action();
      await refresh();
      notify(success);
    } catch (value) {
      fail(value);
    } finally {
      setBusy(false);
    }
  }

  async function login() {
    setBusy(true);
    try {
      const walletAddress = await connectWallet();
      const { message: loginMessage } = await api.getNonce(walletAddress);
      const signature = await signLogin(walletAddress, loginMessage);
      const session = await api.verify(walletAddress, loginMessage, signature);
      api.setToken(session.token);
      await api.flushChainActions();
      setAddress(walletAddress);
      await refresh();
      const referrer = new URLSearchParams(window.location.search).get('ref');
      if (referrer && isAddress(referrer) && referrer.toLowerCase() !== walletAddress.toLowerCase()) {
        await api.bindReferral(referrer).catch(() => undefined);
        await refresh();
      }
      notify('登录成功，牧场进度已从服务器恢复');
    } catch (value) {
      api.clearToken();
      fail(value);
    } finally {
      setBusy(false);
    }
  }

  async function mintNft() {
    if (!address) return;
    const contractAddress = import.meta.env.VITE_NFT_CONTRACT_ADDRESS;
    if (!contractAddress || !isAddress(contractAddress) || !sale) {
      fail(new Error('尚未配置测试网 NFT 合约地址'));
      return;
    }
    setBusy(true);
    try {
      const gasPrice = await publicClient.getGasPrice();
      const hash = await walletClient(address).writeContract({
        address: contractAddress,
        abi: ranchNftAbi,
        functionName: 'mint',
        args: [1n],
        value: sale.price,
        gasPrice,
        account: address,
        chain: nstChain
      });
      await publicClient.waitForTransactionReceipt({ hash });
      const [nextSale] = await Promise.all([loadSale(contractAddress), refresh()]);
      setSale(nextSale);
      notify(`创世 NFT 铸造成功：${hash.slice(0, 10)}…`);
    } catch (value) {
      fail(value);
    } finally {
      setBusy(false);
    }
  }

  if (!state) {
    return (
      <main className="shell welcome">
        <section className="hero-card">
          <div className="brand"><span>牧野</span><small>NST RANCH</small></div>
          <div className="hero-art hero-cinematic"><img src="/game-art/farm-valley-v2.webp" alt="牧野晨雾山谷" /><div><b>LIVE WORLD</b><span>KAVA TESTNET · SEASON 01</span></div></div>
          <p className="eyebrow">NSTCHAIN SOCIAL GAMEFI</p>
          <h1>把一片荒野，<br />经营成链上家园</h1>
          <p>连接钱包即可恢复云端牧场。播种、收获、拜访邻居，并用创世 NFT 解锁永久权益。</p>
          <button className="primary large" disabled={busy} onClick={login}>{busy ? '等待钱包确认…' : '连接钱包进入牧场'}</button>
          <small className="security-note">签名仅用于登录，不会产生 Gas 费用</small>
        </section>
        {error && <div className="toast error">{error}</div>}
      </main>
    );
  }

  return (
    <main className="shell app-shell">
      <header className="topbar">
        <div className="brand compact"><span>牧野</span><small>NST RANCH</small></div>
        <div className="wallet-chip"><i />{shortAddress(state.player.address)}</div>
      </header>

      <section className="player-strip">
        <div className="avatar">🧑‍🌾</div>
        <div><strong>{state.player.displayName}</strong><small>LV.{state.player.level} · {state.player.xp} XP</small></div>
        <div className="currency"><span>🪙 {state.player.coins}</span><span>{state.nft.isHolder ? '💎 创世会员' : '普通牧场主'}</span></div>
      </section>

      {tab === 'farm' && (
        <ChainFarm address={address!} nftHolder={state.nft.isHolder} weather={state.world?.weather} onProgress={refresh} notify={notify} fail={fail} />
      )}

      {tab === 'nft' && (
        <section className="page nft-page">
          <p className="eyebrow">GENESIS COLLECTION</p>
          <h2>创世牧场通行证</h2>
          <div className="nft-visual nft-cinematic"><img src="/game-art/genesis-member-pass.webp" alt="牧野创世会员通行证" /><div className="nft-badge">GENESIS<br /><b>#00001</b></div><div className="nft-caption"><small>NST RANCH</small><strong>创世会员</strong></div></div>
          <div className="sale-stats">
            <div><small>铸造进度</small><b>{sale ? `${sale.minted} / ${sale.supply}` : '等待合约配置'}</b></div>
            <div><small>单枚价格</small><b>{sale ? `${formatEther(sale.price)} KAVA` : '--'}</b></div>
          </div>
          <div className="benefits"><h3>当前链上权益</h3><ul><li>✨ ERC-1155 收获物 +10%</li><li>🪙 获得项目方配置的 ERC-20 奖励</li><li>🔎 编号与元数据可验证</li><li>🏅 后续活动资格预留</li></ul></div>
          <button className="primary large" disabled={busy || !sale?.active || state.nft.isHolder} onClick={mintNft}>
            {state.nft.isHolder ? '已持有创世 NFT' : sale?.active ? '铸造创世 NFT' : '测试网销售尚未开启'}
          </button>
          <ProjectTokenPanel address={address!} notify={notify} fail={fail} />
          <p className="legal-note">当前仅用于 NSTChain 测试网功能验收，不构成真实投资或收益承诺。</p>
        </section>
      )}

      {tab === 'social' && (
        <section className="page social-page">
          <p className="eyebrow">NEIGHBORHOOD</p><h2>牧野邻里</h2>
          <button className="social-cinematic market-gateway" onClick={() => setTab('market')} aria-label="进入暮光集市">
            <img src="/game-art/social-town-v2.webp" alt="牧野社交广场" />
            <div><b>暮光集市</b><span>拜访 · 加工 · 订单 · 玩家交易</span><em>进入集市 →</em></div>
          </button>
          <button className="social-cinematic valley-gateway" onClick={() => setTab('world')} aria-label="进入山谷生活">
            <img src="/game-art/farm-valley-v2.webp" alt="山谷生活广场" />
            <div><b>山谷生活</b><span>聊天 · 公会 · 赛季 · 天气 · 动物</span><em>进入山谷 →</em></div>
          </button>
          <div className="invite-card"><span>🤝</span><div><b>邀请好友加入牧野</b><p>好友首次登录可获得 20 金币</p></div><button onClick={() => navigator.clipboard.writeText(`${window.location.origin}?ref=${state.player.address}`).then(() => notify('邀请链接已复制')).catch(fail)}>复制链接</button></div>
          <div className="assist-box"><h3>邻居拜访打卡</h3><p>每天可拜访每位邻居一次，双方获得 5 枚服务端友情金币；不会篡改链上作物成熟时间。</p><div><input value={neighbor} onChange={(event) => setNeighbor(event.target.value)} placeholder="输入邻居 0x 钱包地址" /><button disabled={busy || !isAddress(neighbor)} onClick={() => run(() => api.assist(neighbor), '拜访成功，双方 +5 友情金币')}>拜访</button></div></div>
          <div className="leaderboard"><div className="section-head"><h3>本周牧场榜</h3><button className="text-button" onClick={() => api.getLeaderboard().then(setLeaderboard).catch(fail)}>刷新</button></div>{leaderboard.map((entry) => <div className="rank-row" key={entry.address}><span>{entry.rank <= 3 ? ['🥇','🥈','🥉'][entry.rank - 1] : entry.rank}</span><div><b>{entry.displayName}</b><small>{shortAddress(entry.address)}</small></div><strong>{entry.xp} XP</strong></div>)}</div>
        </section>
      )}

      {tab === 'market' && (
        <Suspense fallback={<section className="page market-page"><p className="market-loading">正在载入暮光集市…</p></section>}>
          <MarketHub address={address!} onBack={() => setTab('social')} notify={notify} fail={fail} />
        </Suspense>
      )}

      {tab === 'world' && (
        <Suspense fallback={<section className="page world-page"><p className="market-loading">正在连接山谷生活…</p></section>}>
          <WorldHub address={address!} onBack={() => setTab('social')} refreshPlayer={refresh} notify={notify} fail={fail} />
        </Suspense>
      )}

      {tab === 'profile' && (
        <section className="page profile-page">
          <p className="eyebrow">FARMER PROFILE</p><h2>牧场主档案</h2>
          <div className="profile-hero"><div className="big-avatar">🧑‍🌾</div><b>{state.player.displayName}</b><span>{shortAddress(state.player.address)}</span></div>
          <label>牧场名称<input value={name} maxLength={16} onChange={(event) => setName(event.target.value)} /></label>
          <button className="primary" disabled={busy || name.trim().length < 2} onClick={() => run(() => api.updateName(name.trim()), '牧场名称已保存')}>保存名称</button>
          <div className="account-grid"><div><small>等级</small><b>LV.{state.player.level}</b></div><div><small>经验</small><b>{state.player.xp}</b></div><div><small>NFT</small><b>{state.nft.isHolder ? '已激活' : '未持有'}</b></div><div><small>网络</small><b>{nstChain.name}</b></div></div>
          <button className="primary tutorial-replay" onClick={() => setTutorialOpen(true)}>重新查看新手引导</button>
          <button className="logout" onClick={() => { api.clearToken(); setState(null); setAddress(null); }}>退出登录</button>
        </section>
      )}

      <nav className="bottom-nav">
        {([
          ['farm', '🌱', '牧场'], ['nft', '💎', 'NFT'], ['social', '🤝', '邻居'], ['profile', '👤', '我的']
        ] as const).map(([id, icon, label]) => <button key={id} className={tab === id || (id === 'social' && (tab === 'market' || tab === 'world')) ? 'active' : ''} onClick={() => setTab(id)}><span>{icon}</span>{label}</button>)}
      </nav>
      <GameAudio />
      <TutorialOverlay open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
      {message && <div className="toast success">{message}</div>}
      {error && <div className="toast error" onClick={() => setError('')}>{error}</div>}
    </main>
  );
}
